import {
  Button,
  Card,
  Checkbox,
  Combobox,
  DatePicker,
  FAIcon,
  Field,
  FieldLabel,
  UserCombobox,
  type ComboboxItem,
} from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useMemo } from 'react'
import { useExportTaxZoneReportForm, type TaxZoneReportType } from '../../hooks'

export const ExportFleetTaxZoneCard: React.FC = () => {
  const translations = useTranslate({
    title: 'fleet.tax-zone-report.title',
    period: 'common.period',
    reportType: 'fleet.tax-zone-report.report-type',
    taxZones: 'fleet.tax-zone-report.tax-zones',
    countryAndTaxZones: 'fleet.tax-zone-report.country-and-tax-zones',
    drivers: 'fleet.tax-zone-report.users',
    excludeNonFleetVehicles: 'fleet.tax-zone-report.exclude-non-fleet-vehicles',
    allDrivers: ['fleet.tax-zone-report.all-users', { n: 0 }],
    export: 'common.export',
  })

  const { loading, form, submit, updateForm, handleExport, handler } =
    useExportTaxZoneReportForm()

  const reportTypeOptions = useMemo<ComboboxItem<TaxZoneReportType>[]>(
    () => [
      { value: 'TAX_ZONES', label: translations.taxZones },
      {
        value: 'COUNTRY_AND_TAX_ZONES',
        label: translations.countryAndTaxZones,
      },
    ],
    [translations.taxZones, translations.countryAndTaxZones]
  )

  return (
    <Card title={translations.title} className="px-6">
      <div className="grid grid-cols-responsive gap-4">
        <Field>
          <FieldLabel>{translations.period}</FieldLabel>

          <DatePicker
            mode="range"
            value={{ from: form.dateStart, to: form.dateEnd }}
            format="PP"
            onChange={value => {
              if (!value?.from || !value.to) return
              updateForm({ dateStart: value.from, dateEnd: value.to })
            }}
          />
        </Field>

        <Field>
          <FieldLabel>{translations.reportType}</FieldLabel>

          <Combobox
            mode="single"
            value={form.reportType}
            items={reportTypeOptions}
            noSearch
            onChange={value => handler('reportType')(value)}
          />
        </Field>

        <Field>
          <FieldLabel>{translations.drivers}</FieldLabel>

          <UserCombobox
            mode="multiple"
            value={form.userIds}
            placeholder={items => translations.allDrivers({ n: items.length })}
            onChange={value => updateForm({ userIds: value })}
          />
        </Field>
      </div>

      <div className="mt-4">
        <label className="flex w-fit cursor-pointer items-center gap-3 text-sm font-medium text-foreground">
          <Checkbox
            size="md"
            value={form.excludeNonFleetVehicles}
            onChange={checked =>
              updateForm({ excludeNonFleetVehicles: !!checked })
            }
          />
          <span>{translations.excludeNonFleetVehicles}</span>
        </label>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          type="button"
          size="lg"
          onClick={submit(handleExport)}
          disabled={loading}
        >
          <FAIcon icon="download" />
          {translations.export}
        </Button>
      </div>
    </Card>
  )
}
